import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@shared/ui';

interface ReconcileDialogProps {
  open: boolean;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void> | void;
}

export function ReconcileDialog({ open, loading = false, onOpenChange, onConfirm }: ReconcileDialogProps) {
  const { t } = useTranslation('cabinet');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (reason.trim().length < 3) {
      setError(t('withdrawals.actions.commentRequired'));
      return;
    }
    setError(null);
    await onConfirm(reason.trim());
    setReason('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setReason('');
          setError(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('withdrawals.reconcile.title')}</DialogTitle>
          <DialogDescription>{t('withdrawals.reconcile.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reconcile-reason">{t('withdrawals.reconcile.reason')}</Label>
          <Textarea id="reconcile-reason" value={reason} rows={3} onChange={(event) => setReason(event.target.value)} />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            {t('withdrawals.actions.back')}
          </Button>
          <Button type="button" className="w-full sm:w-auto" loading={loading} onClick={() => void submit()}>
            {t('withdrawals.reconcile.action')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
